import { difference, mapValues, uniq } from 'lodash-es';
import { parseZonedDateTime } from '@internationalized/date';
import { differenceInMilliseconds } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { enUS } from 'date-fns/locale/en-US';
import { AircraftMarkerListItem, AircraftStatusEnum } from '@lp/features/RoutePlanner/types';
import { convertDurationToHours } from '@lp/features/RoutePlanner/utils/route';
import {
  CaseAircraftResource,
  CaseResource,
  CaseResourceCombined,
  CaseResourceType,
  ResourceLogisticsStatus,
} from '@/api/apis/case';
import {
  CandidateMatchType,
  CaseMatchType,
  CompositeShiftType,
  CSAssignmentCandidate,
  Employee,
  EmployeeComposed,
  EmployeeRole,
  OcsConsoleData,
  RSAssignmentCandidate,
} from '@/api/apis/resources';
import { getConsoleSerialNumber, getFullName } from '@/utils/helpers';
import {
  csStatusName,
  RESOURCE_STATUS_TEXT_MAP,
  resourceLogisticsStatusNames,
  rsStatusName,
} from '@/constants/resources.constants';
import { EMPLOYEE_ROLE_NAMES } from '@/constants/user.constants';
import { ShiftType } from '@/api/apis/resources/schedule';
import { RouteLegType, RouteLegTypeEnum } from '@/api/apis/route';
import { RoutePlanResources, RouteResourcesType } from '@/types/route';

type GetResourceDataProps = {
  resource?: CaseResourceCombined | null;
  defaultTitle: string;
};

const STATUS_PER_RESOURCE_MAP: Partial<Record<CaseResourceType, Partial<Record<ResourceLogisticsStatus, string>>>> = {
  [CaseResourceType.OCSConsole]: resourceLogisticsStatusNames,
  [CaseResourceType.ClinicalSpecialist]: csStatusName,
  [CaseResourceType.Surgeon]: rsStatusName,
};

export const getResourceStatus = (resource?: CaseResourceCombined | null) => {
  const resourceTextMap = resource?.resourceType
    ? STATUS_PER_RESOURCE_MAP[resource.resourceType] || RESOURCE_STATUS_TEXT_MAP
    : RESOURCE_STATUS_TEXT_MAP;

  return {
    statusText: resource?.logisticsStatus ? resourceTextMap[resource.logisticsStatus] : '',
    statusType: resource?.logisticsStatus,
  };
};

const getEmployeePlaceName = (
  resourceData: EmployeeComposed,
  type: EmployeeRole,
  resolveSubdivisionName?: (stateCode?: string | null, countryCode?: string | null) => string | undefined,
) => {
  if (!resourceData.baseLocation && !resourceData.homeLocation) {
    return '';
  }

  if (type === EmployeeRole.ClinicalSpecialist) {
    return resourceData.baseLocation?.code;
  }

  const { city, state, country } = resourceData.homeLocation || {};
  const stateName = resolveSubdivisionName?.(state, country) ?? state ?? country ?? '';

  return `${city}, ${stateName}`;
};

export const getResourceEmployeeData = ({
  defaultTitle,
  resource,
  resourceData,
  type,
  resolveSubdivisionName,
}: GetResourceDataProps & {
  resourceData?: EmployeeComposed | Employee | null;
  type: EmployeeRole;
  resolveSubdivisionName?: (stateCode?: string | null, countryCode?: string | null) => string | undefined;
}) => {
  const isAssigned = !!resourceData;
  let title = defaultTitle;
  let imageUrl = '';
  let subTitle = '';

  if (resourceData) {
    title = getFullName(resourceData.firstName, resourceData.lastName);
    imageUrl = resourceData.profilePhoto || '';
    const role = resourceData.roles?.find((r) => r === type) || '';
    const place = getEmployeePlaceName(resourceData as EmployeeComposed, type, resolveSubdivisionName);
    subTitle = `${role ? `${EMPLOYEE_ROLE_NAMES[role]}, ` : ''}${place}`;
  }
  const status = getResourceStatus(resource);

  return { isAssigned, title, imageUrl, subTitle, id: resource?.resourceId, type: resource?.resourceType, ...status };
};

export const getResourceOscDeviceData = ({
  defaultTitle,
  resource,
  resourceData,
}: GetResourceDataProps & { resourceData?: OcsConsoleData | null }) => {
  const isAssigned = !!resourceData;
  let title = defaultTitle;
  let imageUrl = '';
  let subTitle = '';
  let organType = '';

  if (resourceData) {
    title = getConsoleSerialNumber(resourceData);
    imageUrl = resourceData.logoPath || '';
    subTitle = resourceData.hub?.code || '';
    organType = resourceData?.organType || '';
  }
  const status = getResourceStatus(resource);

  return {
    isAssigned,
    title,
    imageUrl,
    subTitle,
    organType,
    id: resource?.resourceId,
    type: resource?.resourceType,
    ...status,
  };
};

export const checkAircraftTimeRangeAvailability = (
  aircraft: AircraftMarkerListItem,
  departureTime: string,
  arrivalTime: string,
) => {
  const availability = aircraft.availabilities[0];
  const departureUtc = parseZonedDateTime(departureTime).toAbsoluteString();
  const arrivalUtc = arrivalTime && parseZonedDateTime(arrivalTime).toAbsoluteString();
  const isDepartureAfterStart =
    departureTime && differenceInMilliseconds(new Date(departureUtc), new Date(availability.startUtc)) >= 0;
  const isArrivalBeforeEnd =
    arrivalTime && differenceInMilliseconds(new Date(availability.endUtc), new Date(arrivalUtc)) >= 0;

  return isDepartureAfterStart && isArrivalBeforeEnd;
};

export const aircraftStatusWeight: Record<AircraftStatusEnum, number> = {
  [AircraftStatusEnum.onDuty]: 1,
  [AircraftStatusEnum.onRest]: 2,
  [AircraftStatusEnum.reserved]: 3,
  [AircraftStatusEnum.unavailable]: 4,
};

export const getCurrentStatusTill = <S extends ShiftType>(
  currentShift: CompositeShiftType<S> | null,
  timezone?: string,
) => {
  if (!currentShift) return 'N/A';

  const now = Date.now();
  const endTime = new Date(currentShift.toUtc).getTime();
  const duration = convertDurationToHours(endTime - now);

  return `${formatInTimeZone(new Date(currentShift.toUtc), timezone!, 'MM/dd/yyyy HH:mm zzz', { locale: enUS })} (${duration}h)`;
};

export const getResourcesOnRoute = (
  mainLegs: RouteLegType[],
  dryRunLegs: RouteLegType[],
  removedIds: Set<string>,
): string[] => {
  const activeLegs = [...mainLegs, ...dryRunLegs].filter((l) => !removedIds.has(l.id));

  return uniq(
    activeLegs
      .filter((l) => l.resources.length)
      .flatMap((l) =>
        l.resources
          .filter((r) =>
            [CaseResourceType.OCSConsole, CaseResourceType.Surgeon, CaseResourceType.ClinicalSpecialist].includes(
              r.resourceType,
            ),
          )
          .map((r) => r.resourceId),
      )
      .filter((id): id is string => !!id),
  );
};

export const getResourcesToRemoveIds = <T>(
  resources: CaseResource<T>[],
  statusList: ResourceLogisticsStatus[],
  forbiddenIds: string[],
): string[] => {
  return resources
    .filter((r) => {
      const isRemovableStatus = statusList.includes(r.logisticsStatus);
      const isForbidden = forbiddenIds.includes(r.resourceId);

      return isRemovableStatus && !isForbidden;
    })
    .map((r) => r.resourceId);
};

export const getAircraftIdsOnRoute = (legs: RouteLegType[], removedIds: Set<string>): string[] => {
  const activeLegs = legs.filter((l) => !removedIds.has(l.id));

  return uniq(
    activeLegs
      .filter((l) => l.legType === RouteLegTypeEnum.TmdxJetAviaLeg)
      .flatMap((l) => l.resources.find((r) => r.resourceType === CaseResourceType.Aircraft)?.resourceId)
      .filter((id): id is string => !!id),
  );
};

export const getAircraftsToRemove = (
  legs: RouteLegType[],
  removedLegsIds: Set<string>,
  aircrafts: CaseResource<CaseAircraftResource>[],
) => {
  const alreadyUsedAircrafts = getAircraftIdsOnRoute(legs, removedLegsIds);
  const aircraftsToRemove = alreadyUsedAircrafts.length
    ? aircrafts.filter((a) => !alreadyUsedAircrafts.includes(a.resourceId))
    : aircrafts;

  return aircraftsToRemove.filter((a) => a.logisticsStatus === ResourceLogisticsStatus.Reserved);
};

const getDiffResourcesIds = (
  left: Record<string, string[]>,
  right: Record<string, string[]>,
): Record<string, string[]> => {
  const clinicalSpecialistIds = difference(left.ClinicalSpecialist || [], right.ClinicalSpecialist || []);
  const surgeonIds = difference(left.Surgeon || [], right.Surgeon || []);
  const ocsConsoleIds = difference(left.OCSConsole || [], right.OCSConsole || []);
  const aircraftIds = difference(left.Aircraft || [], right.Aircraft || []);

  return {
    ...(clinicalSpecialistIds.length
      ? {
          clinicalSpecialistIds,
        }
      : {}),
    ...(surgeonIds.length
      ? {
          surgeonIds,
        }
      : {}),
    ...(ocsConsoleIds.length
      ? {
          ocsConsoleIds,
        }
      : {}),
    ...(aircraftIds.length
      ? {
          aircraftIds,
        }
      : {}),
  };
};

const getMappedResources = (
  resourcesList: Omit<RouteResourcesType, 'Crew' | 'Aircraft'>,
  filterStatusCb: (resourceStatus: ResourceLogisticsStatus) => boolean,
): Record<keyof Omit<RouteResourcesType, 'Crew' | 'Aircraft'>, string[]> => {
  return mapValues(resourcesList, (resources) => {
    return resources?.filter((r) => filterStatusCb(r.logisticsStatus)).map((r) => r.resourceId);
  });
};

type GetSuggestedResourcesToApplyReturn = {
  reserveResources: Record<string, string[]>;
  removeResources: Record<string, string[]>;
};

export const getSuggestedResourcesToApply = (
  caseResources: Omit<RouteResourcesType, 'Crew' | 'Aircraft'>,
  suggestedResources: Omit<Partial<RoutePlanResources>, 'Aircraft'>,
): GetSuggestedResourcesToApplyReturn => {
  const mappedCaseResources = getMappedResources(
    caseResources,
    (r) =>
      ![ResourceLogisticsStatus.OCSDone, ResourceLogisticsStatus.Done, ResourceLogisticsStatus.CancelledInMao].includes(
        r,
      ),
  );
  const reservedCaseResources = getMappedResources(caseResources, (r) =>
    [ResourceLogisticsStatus.Reserved, ResourceLogisticsStatus.BookingSent, ResourceLogisticsStatus.Booked].includes(r),
  );

  const mappedSuggestedResources = mapValues(suggestedResources, (resource) => {
    return resource?.resourceId ? [resource.resourceId] : [];
  });

  return {
    reserveResources: getDiffResourcesIds(mappedSuggestedResources, mappedCaseResources),
    removeResources: getDiffResourcesIds(reservedCaseResources, mappedSuggestedResources),
  };
};

export const getIsResourceAvailableToUse = (
  resource: CSAssignmentCandidate | RSAssignmentCandidate | null,
): boolean => {
  if (!resource) return false;

  const isAlreadyAssigned = resource.match.caseType === CaseMatchType.AlreadyAssigned;
  const isAlreadyReserved = resource.match.caseType === CaseMatchType.AlreadyReserved;
  const isAssignmentCandidate = resource.match.caseType === CaseMatchType.AssignmentCandidate;
  const isAvailableByMatchType = resource.match.matchType !== CandidateMatchType.Miss;
  const canBeUsedOnCase = isAssignmentCandidate && isAvailableByMatchType;

  return isAlreadyReserved || isAlreadyAssigned || canBeUsedOnCase;
};
